import React, { useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import * as XLSX from "xlsx";
import { useAppContext } from "../../AppContext";
import productRoute from "../../services/productRoute";

const EXPECTED_HEADERS = [
  "_id",
  "productName",
  "size",
  "currentSellingPrice",
  "costPrice",
  "category",
];

const ImportModal = ({ showImport, setShowImport }) => {
  const { showToast } = useAppContext();
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const { uploadProduct } = productRoute();

  const resetUpload = () => {
    setFileName(null);
    setFileObj(null);
    setFileData(null);
    const input = document.getElementById("input-file-upload");
    if (input) input.value = "";
  };

  const debugXLSXData = (workbook, worksheet, data) => {
    if (data.length > 0) {
      const rawHeaders = Object.keys(data[0]);

      const emptyRows = data.filter((row) =>
        Object.values(row).every(
          (val) => val === null || val === undefined || val === "" || val === 0
        )
      );

      // Check for costPrice variations
      const costPriceHeaders = rawHeaders.filter(
        (h) => h.toLowerCase().includes("cost") || h.toLowerCase().includes("price")
      );
    }
  };

  const cleanHeaders = (data) => {
    if (!data || data.length === 0) return data;

    return data.map((row) => {
      const cleanedRow = {};
      Object.keys(row).forEach((key) => {
        // Remove BOM, trim whitespace, remove invisible characters
        const cleanKey = key
          .replace(/^\uFEFF/, "") // Remove BOM
          .trim()
          .replace(/[\u200B-\u200D\uFEFF\r\n]/g, ""); // Remove invisible chars

        cleanedRow[cleanKey] = row[key];
      });
      return cleanedRow;
    });
  };

  const validateData = (data) => {
    const errors = [];

    // Check if file has data
    if (!data || !data.length) {
      errors.push("File is empty or has no data rows");
    }

    if (errors.length > 0) {
      showToast({ toastText: errors.join(". "), isSuccess: false });
      return false;
    }

    // Clean the data first
    const cleanedData = cleanHeaders(data);

    // Get actual headers from cleaned data
    const actualHeaders = Object.keys(cleanedData[0]);

    // Check for missing headers (must match EXACTLY)
    const missingHeaders = EXPECTED_HEADERS.filter(
      (expectedHeader) => !actualHeaders.includes(expectedHeader)
    );

    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
      errors.push(`Found headers: ${actualHeaders.join(", ")}`);
    }

    // Create header mapping (must match EXACTLY)
    const headerMapping = {};
    EXPECTED_HEADERS.forEach((expectedHeader) => {
      if (actualHeaders.includes(expectedHeader)) {
        headerMapping[expectedHeader] = expectedHeader;
      }
    });

    // Filter out completely empty rows
    const nonEmptyData = cleanedData.filter((row) =>
      Object.values(row).some((val) => val !== null && val !== undefined && val !== "" && val !== 0)
    );

    if (nonEmptyData.length === 0) {
      errors.push("No valid data rows found in file");
    }

    // Check costPrice column specifically
    const costPriceHeader = headerMapping["costPrice"];
    if (costPriceHeader) {
      // Check if costPrice has any values
      const rowsWithCostPrice = nonEmptyData.filter((row) => {
        const costPrice = row[costPriceHeader];
        return (
          costPrice !== null &&
          costPrice !== undefined &&
          costPrice !== "" &&
          costPrice !== 0 &&
          costPrice.toString().trim() !== ""
        );
      });

      if (rowsWithCostPrice.length === 0) {
        errors.push("No data to upload. Cost Price column is empty");
      } else {
        // Collect all row validation errors
        const rowErrors = [];
        for (let i = 0; i < rowsWithCostPrice.length; i++) {
          const row = rowsWithCostPrice[i];
          const rowIndex = nonEmptyData.indexOf(row) + 2; // +2 for 1-based indexing and header row

          const id = row[headerMapping["_id"]];
          const productName = row[headerMapping["productName"]];
          const currentSellingPrice = row[headerMapping["currentSellingPrice"]];

          const missingFields = [];

          if (!id || !id.toString().trim()) {
            missingFields.push("_id");
          }

          if (!productName || !productName.toString().trim()) {
            missingFields.push("productName");
          }

          if (!currentSellingPrice || !currentSellingPrice.toString().trim()) {
            missingFields.push("currentSellingPrice");
          }

          if (missingFields.length > 0) {
            rowErrors.push(`Row ${rowIndex}: Missing ${missingFields.join(", ")}`);
          }
        }

        // Add row errors to main errors array
        if (rowErrors.length > 0) {
          errors.push("");
          errors.push(...rowErrors);
        }
      }
    }

    // If there are any errors, show them all together
    if (errors.length > 0) {
      const errorMessage = errors.join("\n• ");
      showToast({
        toastText: `${errorMessage}`,
        isSuccess: false,
      });
      return false;
    }

    // Store cleaned data for upload
    setFileData(nonEmptyData);
    return true;
  };

  const handleFile = async (file, inputElement) => {
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "xlsx") {
      showToast({ toastText: "Only XLSX files are allowed!", isSuccess: false });
      if (inputElement) inputElement.value = "";
      return;
    }

    try {
      // Read file as array buffer for better handling
      const arrayBuffer = await file.arrayBuffer();

      // Parse with robust options
      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
        cellText: true,
        cellDates: true,
        raw: false, // Get formatted values
        codepage: 65001, // UTF-8
      });

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        showToast({ toastText: "No worksheets found in the file!", isSuccess: false });
        resetUpload();
        return;
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      if (!worksheet || !worksheet["!ref"]) {
        showToast({ toastText: "Worksheet is empty or corrupted!", isSuccess: false });
        resetUpload();
        return;
      }

      // Convert to JSON with options to handle edge cases
      const data = XLSX.utils.sheet_to_json(worksheet, {
        defval: "", // Default value for empty cells
        blankrows: false, // Skip completely blank rows
        raw: false, // Get formatted text values
        dateNF: "yyyy-mm-dd", // Date format
      });

      // Debug the parsed data
      debugXLSXData(workbook, worksheet, data);

      if (validateData(data)) {
        setFileName(file.name);
        setFileObj(file);
        // fileData is set in validateData after cleaning
      } else {
        resetUpload();
      }
    } catch (error) {
      showToast({
        toastText: `Error reading file: ${error.message}. Please ensure the file is a valid XLSX format.`,
        isSuccess: false,
      });
      resetUpload();
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const input = document.getElementById("input-file-upload");
      handleFile(e.dataTransfer.files[0], input);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0], e.target);
    }
  };

  const handleUpload = () => {
    if (!fileObj) return;
    const formData = new FormData();
    formData.append("file", fileObj);
    uploadProduct(setImportLoading, showToast, formData);
  };

  return (
    <Modal show={showImport} onHide={() => setShowImport(false)} centered>
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body className="justify-content-center text-center pb-5">
        <h3 className="mb-4" style={{ fontWeight: "600" }}>
          Upload File
        </h3>
        {importLoading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form
            onDragEnter={handleDrag}
            onSubmit={(e) => e.preventDefault()}
            className="d-flex flex-column align-items-center"
          >
            <input
              type="file"
              id="input-file-upload"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={handleChange}
            />

            {!fileName ? (
              <label
                htmlFor="input-file-upload"
                className={`p-5 ${
                  dragActive ? " border border-2 rounded-3 border-success" : "border-none"
                }`}
                style={{
                  width: "100%",
                  cursor: "pointer",
                  backgroundColor: dragActive ? "#f8fff5" : "transparent",
                }}
              >
                <div className="d-flex flex-column align-items-center">
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      backgroundColor: "#45a049",
                      color: "white",
                      fontSize: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <i className="bi bi-cloud-upload"></i>
                  </div>
                  <h6 className="mb-2 text-muted">
                    <span className="text-success fw-bold">Click to upload</span> or drag and drop
                    the XLSX file
                  </h6>
                  <small className="text-muted">
                    Required headers: {EXPECTED_HEADERS.join(", ")}
                  </small>
                </div>
              </label>
            ) : (
              <div className="d-flex flex-column align-items-center my-5">
                <h6 className="fw-bold mb-2">{fileName}</h6>
                <div className="d-flex justify-content-center gap-2 mt-4">
                  <Button variant="outline-danger" size="sm" onClick={resetUpload}>
                    Cancel
                  </Button>
                  <Button size="sm" disabled={!fileData} onClick={handleUpload}>
                    Upload
                  </Button>
                </div>
              </div>
            )}

            {dragActive && (
              <div
                id="drag-file-element"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                }}
              ></div>
            )}
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ImportModal;
